var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useEffect, useRef } from 'react';
import customEventHandler from '../customEvents';
import { captureMultiSelectStartPositions, captureTextStartPositions, commitMultiSelectMove, commitTextMove, } from '../multiDragHelper';
import { beginMultiSelectSaveBatch, registerMultiSelectSaveEntries, } from '../multiSelectSaveCoordinator';
import { saveState } from '../stateManagerHelper';
/**
 * Mount exactly once (in GameController). Owns movement + the batched undo entry for every
 * selected item OTHER than the drag source — the source keeps moving itself via its own local
 * drag logic, same as before this refactor.
 */
export function useMultiSelectFollowerEngine(manipulative, setManipulative, text, setText, selectedTool, canvasWidth, canvasHeight) {
    var manipulativeRef = useRef(manipulative);
    var textRef = useRef(text);
    var selectedToolRef = useRef(selectedTool);
    var canvasWidthRef = useRef(canvasWidth);
    var canvasHeightRef = useRef(canvasHeight);
    manipulativeRef.current = manipulative;
    textRef.current = text;
    selectedToolRef.current = selectedTool;
    canvasWidthRef.current = canvasWidth;
    canvasHeightRef.current = canvasHeight;
    var snapshotRef = useRef(null);
    var textSnapshotRef = useRef(null);
    var sourceIdRef = useRef(null);
    // Monotonic per-gesture id. A leader can be re-dragged repeatedly (fast "stop, then drag
    // again" on the SAME widget), so sourceIdRef alone can't tell "this gesture" apart from "a
    // different gesture with the same leader" — this counter can. See deferredHardResetFallback.
    var gestureIdRef = useRef(0);
    var loggedMoveForGestureRef = useRef(0);
    useEffect(function () {
        var onStart = function (payload) {
            gestureIdRef.current += 1;
            sourceIdRef.current = payload.sourceId;
            snapshotRef.current = captureMultiSelectStartPositions(manipulativeRef.current, selectedToolRef.current, payload.sourceId, payload.excludeIds);
            textSnapshotRef.current = captureTextStartPositions(textRef.current, selectedToolRef.current, payload.sourceId, payload.excludeIds);
            // eslint-disable-next-line no-console
            console.log('[ResizeHolderDrag debug] FollowerEngine onStart', JSON.stringify({
                sourceId: payload.sourceId,
                excludeIds: payload.excludeIds,
                selectedTool: __spreadArray([], selectedToolRef.current, true),
                capturedIds: snapshotRef.current.map(function (s) { return s.id; }),
                droppedIds: selectedToolRef.current.filter(function (id) {
                    var _a;
                    return id !== payload.sourceId &&
                        !((_a = payload.excludeIds) === null || _a === void 0 ? void 0 : _a.includes(id)) &&
                        !snapshotRef.current.some(function (s) { return s.id === id; });
                }),
            }));
            // This hook mounts exactly once app-wide, so it's the one reliable place to reset the
            // shared save-batch collector for a new gesture regardless of which widget leads.
            beginMultiSelectSaveBatch(payload.sourceId);
        };
        var onMove = function (payload) {
            var _a, _b;
            var snapshot = snapshotRef.current;
            var textSnapshot = textSnapshotRef.current;
            var hasManipulativeFollowers = !!snapshot && snapshot.length > 0;
            var hasTextFollowers = !!textSnapshot && textSnapshot.length > 0;
            if (!hasManipulativeFollowers && !hasTextFollowers)
                return;
            if (payload.sourceId !== sourceIdRef.current)
                return;
            if (loggedMoveForGestureRef.current !== gestureIdRef.current) {
                loggedMoveForGestureRef.current = gestureIdRef.current;
                // eslint-disable-next-line no-console
                console.log('[ResizeHolderDrag debug] FollowerEngine onMove committing (first move this gesture)', JSON.stringify({
                    sourceId: payload.sourceId,
                    deltaX: payload.deltaX,
                    deltaY: payload.deltaY,
                    followerCount: ((_a = snapshot === null || snapshot === void 0 ? void 0 : snapshot.length) !== null && _a !== void 0 ? _a : 0) + ((_b = textSnapshot === null || textSnapshot === void 0 ? void 0 : textSnapshot.length) !== null && _b !== void 0 ? _b : 0),
                }));
            }
            if (hasTextFollowers) {
                setText(commitTextMove(textSnapshot, payload.deltaX, payload.deltaY));
            }
            if (!hasManipulativeFollowers)
                return;
            setManipulative(commitMultiSelectMove(snapshot, payload.deltaX, payload.deltaY, canvasWidthRef.current, canvasHeightRef.current));
        };
        var onEnd = function (payload) {
            var _a, _b;
            var snapshot = snapshotRef.current;
            var textSnapshot = textSnapshotRef.current;
            var entries = [];
            if (payload.sourceId === sourceIdRef.current && textSnapshot && textSnapshot.length > 0) {
                var latestText = textRef.current;
                for (var _i = 0, textSnapshot_1 = textSnapshot; _i < textSnapshot_1.length; _i++) {
                    var s = textSnapshot_1[_i];
                    var idx = Number(s.id.split('_')[1]);
                    var current = (_b = (_a = latestText[idx]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.position;
                    if (!current)
                        continue;
                    entries.push({ type: 'text', id: String(idx), propertyName: 'position', old: s.position, new: current });
                }
            }
            if (snapshot && snapshot.length > 0 && payload.sourceId === sourceIdRef.current) {
                // Final positions already reflect the last `onMove` delta via `manipulative` state;
                // re-derive the batch save from the last committed delta stored on the snapshot items.
                var latest_1 = manipulativeRef.current;
                entries.push.apply(entries, snapshot
                    .map(function (s) {
                    var _a, _b;
                    var item = latest_1.find(function (m) { return m.data.id === s.id; });
                    var current;
                    if (item) {
                        current =
                            s.propertyName === 'groupCenter'
                                ? item.data.groupCenter
                                : s.propertyName === 'position'
                                    ? item.data.position
                                    : item.data.center;
                    }
                    else if (s.propertyName === 'position') {
                        // Dealt cards live nested inside deck.data.cards[], not as their own
                        // top-level manipulative entry, so the direct lookup above always misses
                        // for them — search decks' nested cards[] instead (mirrors the equivalent
                        // lookup in multiDragHelper.ts's captureMultiSelectStartPositions/commitMultiSelectMove).
                        var deck = latest_1.find(function (m) { var _a; return (_a = m.data.cards) === null || _a === void 0 ? void 0 : _a.some(function (c) { return c.id === s.id; }); });
                        current = deck && ((_a = deck.data.cards.find(function (c) { return c.id === s.id; })) === null || _a === void 0 ? void 0 : _a.position);
                    }
                    if (!current)
                        return null;
                    return {
                        type: s.id,
                        id: (_b = s.id.split('_')[1]) !== null && _b !== void 0 ? _b : s.id,
                        propertyName: s.propertyName,
                        old: s.center,
                        new: current,
                    };
                })
                    .filter(Boolean));
            }
            if (entries.length > 0 && !registerMultiSelectSaveEntries(payload.sourceId, entries)) {
                saveState(entries);
            }
            hardReset();
        };
        var hardReset = function () {
            snapshotRef.current = null;
            textSnapshotRef.current = null;
            sourceIdRef.current = null;
        };
        // Fallback ONLY for a gesture that ends without 'multiSelectMoveEnd' ever firing (e.g. mouse
        // released in a way that skips the leader widget's own mouseup handler). Deferred via
        // setTimeout(0) — NOT called synchronously — because this listener is registered once here
        // at mount (very early), while each leader widget's own mouseup->onUp listener is registered
        // fresh at that drag's mousedown (much later). Same-target/same-type DOM listeners fire in
        // registration order, so calling hardReset() synchronously here would run BEFORE the widget's
        // own onUp even executes and dispatches 'multiSelectMoveEnd' — nulling sourceIdRef/snapshotRef
        // before onEnd's `payload.sourceId === sourceIdRef.current` check ever runs, silently
        // dropping every central-engine follower save. Deferring lets the same-tick synchronous
        // 'multiSelectMoveEnd' dispatch (and this hook's own onEnd/hardReset) complete first.
        // Deliberately does NOT call cancelMultiSelectSaveBatch() here (that would have the exact
        // same premature-race problem for the save coordinator) — any legitimately-registered
        // entries are still safely flushed by the coordinator's own deferred flush, or synchronously
        // flushed by the next gesture's beginMultiSelectSaveBatch if this one never got a proper end.
        //
        // Guard against staleness: this callback fires on EVERY mouseup/touchend, including ones that
        // already ended cleanly via onEnd. If a new gesture starts (onStart) before this deferred
        // callback runs — a fast "stop, then immediately drag again" — a naive unconditional
        // hardReset() here would wipe out the NEW gesture's just-captured snapshot/sourceId, leaving
        // every follower silently frozen (onMove's `!snapshot` early-return) for the rest of that
        // drag even though the mouse is still down and moving.
        //
        // IMPORTANT: the guard compares gestureIdRef (a monotonic counter bumped once per onStart),
        // NOT sourceIdRef. The same leader widget is very often re-dragged immediately (that's
        // exactly what "stop, then drag again" does) — sourceId alone can't distinguish "this same
        // gesture, still running" from "a brand new gesture that happens to share the same leader."
        // A first fix attempt using sourceId equality was verified insufficient by a repro capture
        // showing this exact false-positive match. gestureId can't collide across gestures, so it
        // closes that gap: the deferred reset only fires if NO onStart has happened since it was
        // scheduled, regardless of which widget leads either gesture.
        var deferredHardResetFallback = function () {
            var staleGestureId = gestureIdRef.current;
            setTimeout(function () {
                if (gestureIdRef.current === staleGestureId)
                    hardReset();
            }, 0);
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        customEventHandler.addEventListener('multiSelectMoveEnd', onEnd);
        window.addEventListener('mouseup', deferredHardResetFallback);
        window.addEventListener('touchend', deferredHardResetFallback);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
            customEventHandler.removeEventListener('multiSelectMoveEnd', onEnd);
            window.removeEventListener('mouseup', deferredHardResetFallback);
            window.removeEventListener('touchend', deferredHardResetFallback);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
/**
 * Called inside a widget's OWN existing move-drag handlers (mousedown/mousemove/mouseup).
 * Wire ONLY into the plain positional-move gesture — never into resize/rotate/angle/cut handlers.
 */
export function useMultiSelectDragSource(id, selectedTool) {
    var startMultiDrag = function () {
        var willBroadcast = selectedTool.length > 1 && selectedTool.includes(id);
        if (willBroadcast) {
            customEventHandler.dispatchEvent('multiSelectMoveStart', { sourceId: id });
        }
    };
    var moveMultiDrag = function (deltaX, deltaY) {
        var willBroadcast = selectedTool.length > 1 && selectedTool.includes(id);
        if (willBroadcast) {
            customEventHandler.dispatchEvent('multiSelectMove', { sourceId: id, deltaX: deltaX, deltaY: deltaY });
        }
    };
    var endMultiDrag = function () {
        if (selectedTool.length > 1 && selectedTool.includes(id)) {
            customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: id });
        }
    };
    return { startMultiDrag: startMultiDrag, moveMultiDrag: moveMultiDrag, endMultiDrag: endMultiDrag };
}
